import { gql, useQuery } from "@apollo/client";
import { datadogLogs } from "@datadog/browser-logs";
import useOrdersAmount from "@repo/shopify/hooks/useOrdersAmount";
import { accountDeleteNotifications } from "@repo/shopify/src/api/deleteAccountNotification";
import type { ISocialLoginMetaObjectQuery } from "@repo/shopify/src/queries/metaobject-gql";
import { socialLoginMetaobjectQuery } from "@repo/shopify/src/queries/metaobject-gql";
import { extractId } from "@repo/shopify/src/utils";
import {
  getNextMonthFirstDate,
  getOneWeekBeforeMonthEnd,
  getSameDayOfPreviousYear,
} from "@repo/shopify/src/utils/order";
import { transformWon } from "@repo/utils/src/number";
import classNames from "classnames";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import Button from "@components/atoms/Button";
import type { TMembership } from "@components/molecules/Card/MembershipCard";
import MembershipCard, { MEMBERSHIP_INFO } from "@components/molecules/Card/MembershipCard";
import Loading from "@components/molecules/Loading";

import Barcode from "@organisms/Account/Barcode";
import ChangePassword from "@organisms/Account/ChangePassword/ChangePassword";
import ChangePasswordSocial from "@organisms/Account/ChangePassword/ChangePasswordSocial";
import Information from "@organisms/Account/Information";

import { initialStateUser } from "@reducers/user";

import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";
import useBrandPay from "@hooks/useBrandPay";

import { LoadingType } from "@const/enum";
import { URLS } from "@const/urls";

import { NOTIFICATIONS } from "@utils/notifications";

import { fetchRemoveSocialList } from "@api/lamdaApi";

const DOMAIN = "TUNE";
const META_OBJECT_ID = "gid://shopify/Metaobject/2629468435";

function AccountInformation() {
  const navigate = useNavigate();

  const { openToast, openModal, closeModal, setModalProgress, showFooter } = useLayout();
  const { customer, updateUser, userLogout, loginLoading, accessToken } = useUser();
  const { t } = useTranslation();
  const { openBrandPaySetting } = useBrandPay();

  // 등급 업데이트 기준 날짜 계산: 매달 1일 00:00 기준 1년 이내의 주문이면서 마지막 7일 이내가 아닌 주문의 실 결제 금액
  const today = new Date();
  const nextMonthStart = getNextMonthFirstDate(today);
  const oneYearAgo = getSameDayOfPreviousYear(nextMonthStart);
  const oneWeekBeforeMonthEnd = getOneWeekBeforeMonthEnd(today);

  const { totalOrderAmount, isOrdersLoading } = useOrdersAmount({
    accessToken,
    startDate: oneYearAgo,
    endDate: oneWeekBeforeMonthEnd,
    lang: "ko",
  });

  /** 소셜로그인으로 가입한 계정 리스트 받아오기 */
  const { data } = useQuery<ISocialLoginMetaObjectQuery>(gql(socialLoginMetaobjectQuery.query), {
    variables: { id: META_OBJECT_ID },
    onError: (error) => {
      datadogLogs.logger.error("[SHOPIFY][Metaobject] Failed to query", error);
      throw new Error(`[SHOPIFY][Metaobject] Failed to query : ${JSON.stringify(error)}`);
    },
  });

  useEffect(() => {
    showFooter(true);
  }, []);

  /** 회원 탈퇴 요청 시 고객 정보 슬랙으로 알림 */
  const handleAccountDelete = async () => {
    if (!customer || !customer.id) return;

    setModalProgress(true);

    const handleDeletionSuccess = () => {
      updateUser(initialStateUser);
      userLogout();
      openToast(NOTIFICATIONS.SUCCESS_DISABLE_CUSTOMER);
      navigate(URLS.HOME);
    };

    if (customer.tags.includes("탈퇴요청")) {
      handleDeletionSuccess();
      return;
    }

    const response = await accountDeleteNotifications({
      domain: DOMAIN,
      customerId: extractId(customer.id),
      email: customer.email || "",
      name: (customer.lastName || "") + (customer.firstName || ""),
    });

    if (response) {
      handleDeletionSuccess();
    }

    setModalProgress(false);
  };

  /** 소셜로그인으로 가입한 계정이 회원 탈퇴 경우 소셜로그인 메타 오브젝트 데이터에서 삭제 */
  const disableCustomerHandle = async () => {
    if (data && Object.keys(data).includes(customer?.email || "")) {
      await handleRemoveSocialList();
    } else {
      await handleAccountDelete();
    }
  };

  const handleRemoveSocialList = async () => {
    const response = await fetchRemoveSocialList(customer?.email || "");
    if (response) {
      await handleAccountDelete();
    }
  };

  const handleDeleteAccount = () => {
    openModal({
      title: "Delete Account",
      description: t("account.info.delete_ask"),
      confirmButton: {
        label: "Okay",
        onClick: () => {
          if (customer.tags.includes("탈퇴요청")) {
            openToast(NOTIFICATIONS.ERROR_ALREADY_DELETE_ACCOUNT);
            return;
          }
          void disableCustomerHandle();
          closeModal();
        },
      },
      cancelButton: {
        label: "Cancel",
        onClick: () => {
          closeModal();
        },
      },
    });
  };

  // 현재 멤버십 등급
  const membership: TMembership = useMemo(() => {
    const customerTags = customer?.tags.map((tag) => tag.toLowerCase()) || [];

    if (customerTags.includes("employee")) return "EMPLOYEE";
    if (customerTags.includes("friends")) return "FRIENDS";

    // membership_ 태그가 존재하는 경우 그에 맞는 등급 반환, 없으면 "WHITE"
    return (
      (customerTags
        .find((tag) => tag.startsWith("membership_"))
        ?.replace("membership_", "")
        .toUpperCase() as TMembership) ?? "WHITE"
    );
  }, [customer, totalOrderAmount]);

  const getNextMembership = (offset: number): TMembership => {
    if (membership === "EMPLOYEE") return "EMPLOYEE";
    if (membership === "FRIENDS") return "FRIENDS";

    const calcAmount = Number(totalOrderAmount.toFixed(2));
    const membershipInfo = Object.entries(MEMBERSHIP_INFO).map(([key, value]) => ({
      name: key,
      value: value.endRange,
    }));
    const filterAmount = membershipInfo.filter((item) => item.value > calcAmount).sort((a, b) => a.value - b.value);

    return filterAmount.length < offset ? "BLACK" : (filterAmount[offset - 1].name as TMembership);
  };

  // 직원 / 지인 인지 판단
  const isEmployee = useMemo(() => membership === "EMPLOYEE" || membership === "FRIENDS", [membership]);

  // 다음 달 멤버십 등급
  const nextMonthMembership: TMembership = useMemo(() => getNextMembership(1), [membership, totalOrderAmount]);
  // 다음 멤버십 등급
  const nextMembership: TMembership = useMemo(() => getNextMembership(2), [membership, totalOrderAmount]);

  // 다음 멤버십까지 남은 금액
  const getAmountLeft = useMemo(() => {
    if (membership === "EMPLOYEE" || membership === "FRIENDS") {
      return 0;
    }

    const nextMembershipAmount = MEMBERSHIP_INFO[nextMembership].startRange;

    return nextMembershipAmount <= totalOrderAmount ? 0 : nextMembershipAmount - totalOrderAmount;
  }, [membership, totalOrderAmount, nextMonthMembership]);

  // 다음 멤버십까지의 퍼센트 계산
  const percent = useMemo(
    () =>
      membership === "EMPLOYEE" || membership === "FRIENDS"
        ? 0
        : (totalOrderAmount / MEMBERSHIP_INFO[nextMonthMembership].endRange) * 100,
    [membership, totalOrderAmount],
  );

  return (
    <>
      {loginLoading ? (
        <Loading type={LoadingType.Content} />
      ) : (
        <section className="tune-column-4 pc:-mt-0 -mt-5 flex flex-col gap-10">
          <div className="flex flex-col gap-5">
            <span>{t("account.info.me")}</span>
            <p>{t("account.info.agree")}</p>
            <Information />
          </div>
        </section>
      )}
    </>
  );
}

export default AccountInformation;
